import {
  isEmbedPageEnabled,
  resolveSubscriptionEntitlements,
} from "./subscriptionPlan";

export const CREATOR_PAGES_FROM = "creator-pages";

export function getComponentsTableName(from?: string) {
  if (from === "embed-search") return "embed_components";
  if (from === CREATOR_PAGES_FROM) return "page_components";
  if (from === "website-template") return "template_components";
  return "stayuptodate_components";
}

export function isCreatorPagesMode(from?: string) {
  return from === CREATOR_PAGES_FROM;
}

/** Website product creators use the template editor, not Creator Pages. */
function isWebsiteProductCreator(userData: any) {
  return resolveSubscriptionEntitlements(userData).hasPages;
}

export function isCreatorPagesFeatureEnabled(userData: any) {
  if (isWebsiteProductCreator(userData)) return false;
  return Boolean(
    userData?.creator_pages_feature ||
      userData?.is_creator_pages_feature ||
      userData?.multi_page_feature ||
      userData?.is_multi_page_feature
  );
}

export function isMultiPageFeatureEnabled(userData: any) {
  if (isWebsiteProductCreator(userData)) return false;
  return Boolean(
    userData?.multi_page_feature || userData?.is_multi_page_feature
  );
}

/** Embed-only users: show Embed under Overview (main-branch sidebar). */
export function shouldShowEmbedInOverviewSidebar(userData: any) {
  return isEmbedPageEnabled(userData) && !isMultiPageFeatureEnabled(userData);
}

/**
 * Multi-page users with Embed access (Growth, or a legacy Embed Page already on):
 * list it under Pages. Enabling multi-page alone must not invent an Embed Page
 * for Starter/Pro.
 */
export function shouldShowEmbedInPagesSidebar(userData: any) {
  return isEmbedPageEnabled(userData) && isMultiPageFeatureEnabled(userData);
}

/** Empty "No pages yet" copy — false when Embed Page already counts as a page. */
export function shouldShowPagesEmptyHint(
  creatorPageCount: number,
  showEmbedInPages: boolean
) {
  return creatorPageCount === 0 && !showEmbedInPages;
}

/** Normalized lifecycle status from `status_expiry` or legacy `status`. */
export function getComponentStatus(component: any): string {
  return String(component?.status_expiry ?? component?.status ?? "")
    .toLowerCase()
    .trim();
}

/** Bootstrapped onboarding carousels use `hidden` and must not appear in UI lists. */
export function isHiddenComponent(component: any): boolean {
  return getComponentStatus(component) === "hidden";
}

/** Default length for auto-generated component route / unique_id (link-in-bio style). */
export const DEFAULT_OPAQUE_ROUTE_TOKEN_LENGTH = 8;

/**
 * Random lowercase alphanumeric route token — same style as link-in-bio
 * "Customize Your Link" endings (letters and numbers only, no dashes).
 */
export function generateOpaqueRouteToken(
  length: number = DEFAULT_OPAQUE_ROUTE_TOKEN_LENGTH
) {
  const alphabet = "abcdefghijklmnopqrstuvwxyz0123456789";
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  let out = "";
  for (let i = 0; i < bytes.length; i += 1) {
    out += alphabet[bytes[i] % alphabet.length];
  }
  return out;
}

/** Creates a new opaque route slug for a component (ignores any passed name). */
export function createComponentSlug(_value?: string) {
  return generateOpaqueRouteToken();
}
